import { getServerRoutes } from 'ctm-builders';

/**
 * Simple loader for getting the vertical app based on environment
 *
 * For dev: load the vertical_import.dev.js file which has all available verticals defined and available for import,
 *          choose the correct one using the EVEREST_VERTICAL_PACKAGE env var
 *
 * For prod: load the vertical_import.prod.js file which only has the vertical matching EVEREST_VERTICAL_PACKAGE env var
 *
 * We do it this way so that docker-compose can spin up multiple dev-local stacks with volume mapping to a ctm_everest
 * checkout without having different verticals generating a vertical_import.prod.js file in the same platform checkout.
 *
 * We don't want to make all verticals available when deploying a single vertical to prod, so we restrict the available
 * verticals to just the one we want.
 *
 * @returns {Object} - The vertical application object - see ctm-vertical-XXX/core/app.js
 */
const loadVerticalApp = () => {
    if (process.env.NODE_ENV === 'development' && process.env.EVEREST_DEPLOY_ENV?.trim() === 'local') {
        // eslint-disable-next-line global-require
        const app = require('../../../vertical_import.dev');
        const packageName = process.env.EVEREST_VERTICAL_PACKAGE;
        const verticalPackage = app.ctmApps[packageName];
        if (verticalPackage?.app == null) {
            throw Error(`Cannot find ${packageName} in imported application, or the application has not compiled due to a syntax issue.`);
        }
        return verticalPackage.app;
    }

    try {
        // do this in a try catch, since the require() seems to run regardless
        // Make sure the if condition is simple enough that the bundler can remove the block
        // eslint-disable-next-line global-require
        return require('../../../vertical_import.prod').ctmApp.app;
    } catch (err) {
        throw new Error("can't import app", { cause: err });
    }
};

/**
 * Simple wrapper around the apps config to provide routes and reducers
 * for the runtime application.
 */
class AppsConfigModel {

    /**
     * Setup apps, routes and reducers based on apps_config
     * @constructs
     * @param {Object} ctmApp - A vertical app object, this should only be passed in during unit tests.
     */
    constructor(ctmApp = null) {
        if (ctmApp === null) {
            this.ctmApp = loadVerticalApp();
        } else {
            // Allow ctmApp to be passed in to make testing with mocks easier
            this.ctmApp = ctmApp;
        }

        this.reducers = this.getReducers();
    }

    /**
     * Return the app name
     * @returns {String} - The name of the current vertical
     */
    getAppName = () => {
        return this.ctmApp.title;
    };

    /**
     * Return the app version
     * @returns {String} - The current version string - for A/B tests - v1, v2 etc or empty if latest
     */
    getVersion = () => {
        return this.ctmApp.models.pageSchemaModel.getSettings().version;
    };

    /**
     * Returns the base url required for the reporter.
     * @returns {String} - the base url for the reporter
     */
    getNodeEndpointBaseUrl = () => {
        let baseUrl = `${this.getAppName()}/journey`;
        const version = this.getVersion();
        if (version) {
            baseUrl = `${baseUrl}/${version}`;
        }
        return baseUrl;
    };

    /**
     * Returns the server configuration for the specified deploy environment
     * @param {String} deployEnv - The deployment environment to get the config for - one of local, dev, uat, preprod or prod
     * @param {String} theme - (optional) The white label code, if applicable. Affects the config retrieved.
     * @returns {Object} - The object containing the backend URLs, theme name and server port
     */
    getServerConfig = (deployEnv, theme) => {
        return this.ctmApp.getServerConfig(deployEnv, theme);
    };

    /**
     * Get the models for a particular vertical
     * @returns {Object} - The set of models for given app or vertical
     */
    getModels = () => {
        return this.ctmApp.models;
    };

    /**
     * Return the full mapping of app titles to reducers for the application
     * @returns {Object} reducers - The complete set of reducers for each app,
     *  mapped to the app title
     */
    getReducers = () => {
        return { vertical: this.ctmApp.reducer };
    };

    /**
     * Check if there is a corresponding route for pathName
     * @param {String} pathName - The path to check
     * @returns {Boolean} - True if there is a matching route for pathName.
     */
    isValidPath = (pathName) => {
        const routes = getServerRoutes();
        const route = routes.find((routeObj) => { return routeObj.path ? routeObj.path === pathName : false; });
        return !!route;
    };

    /**
     * Only return the snippets for a valid path, return null if the pathName is a 404.
     * @param {String} pathName - The current page pathName
     * @returns {SnippetModel} The SnippetModel for the current vertical
     */
    getSnippets = (pathName) => {
        return this.isValidPath(pathName) ? this.ctmApp.snippets : null;
    };

}

/**
 * Lazily initialize the AppsConfigModel instance to avoid a circular module dependency.
 *
 * The circular chain is:
 *   routes.js → ctm-builders → form/schema_model.js → ctm-components → page.js
 *   → reporter.js → api.js → app_config_model.js → new AppsConfigModel()
 *   → loadVerticalApp() → ctm-vertical-car/core/models.js → ctm-builders.FormSchemaModel
 *
 * Because ctm-builders is still mid-load at that point, its re-export getters resolve
 * to undefined variables, causing a TypeError. Deferring instantiation until the first
 * property access ensures all modules are fully loaded before the vertical is loaded.
 */
let _configModelInstance = null;

// eslint-disable-next-line func-style
function _getOrCreateInstance() {
    if (_configModelInstance === null) {
        _configModelInstance = new AppsConfigModel();
    }
    return _configModelInstance;
}

const configModel = new Proxy({}, {
    get(_, prop) {
        const instance = _getOrCreateInstance();
        const value = instance[prop];
        if (typeof value === 'function') {
            return value.bind(instance);
        }
        return value;
    },
    set(_, prop, value) {
        _getOrCreateInstance()[prop] = value;
        return true;
    }
});

export { configModel };
